import React, { useState, useEffect } from 'react'
import { Plus, Key, Copy, Trash2, Calendar, Users, Eye, Upload, Shield, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'

const InvitesTab = () => {
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newInvite, setNewInvite] = useState({
    permissions: 'viewer',
    expiryDays: 30,
    customCode: ''
  })

  const permissionOptions = [
    { value: 'viewer', label: 'Viewer', icon: Eye, description: 'Can only view videos' },
    { value: 'uploader', label: 'Uploader', icon: Upload, description: 'Can view and upload videos' },
    { value: 'admin', label: 'Admin', icon: Shield, description: 'Full admin access' }
  ]

  const expiryOptions = [
    { value: 7, label: '7 Days' },
    { value: 30, label: '30 Days' },
    { value: 90, label: '90 Days' },
    { value: 0, label: 'Never Expires' }
  ]

  useEffect(() => {
    loadInvites()
  }, [])

  const loadInvites = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/admin-list-invites', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load invites')
      }

      const data = await response.json()
      setInvites(data.invites || [])
    } catch (error) {
      console.error('Load invites error:', error)
      toast.error('Failed to load invitations')
    } finally {
      setLoading(false)
    }
  }

  const generateInviteCode = () => {
    const codes = ['FAMILY', 'SUMMER', 'HOLIDAY', 'BIRTHDAY', 'REUNION', 'VACATION']
    const randomCode = codes[Math.floor(Math.random() * codes.length)]
    const year = new Date().getFullYear()
    return `${randomCode}${year}`
  }

  const createInvite = async () => {
    try {
      const token = localStorage.getItem('token')
      const inviteCode = newInvite.customCode.trim() || generateInviteCode()
      
      const response = await fetch('/.netlify/functions/admin-create-invite', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: inviteCode,
          permissions: newInvite.permissions,
          expiryDays: newInvite.expiryDays
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create invite')
      }

      toast.success('Invitation created successfully!')
      setShowCreateModal(false)
      setNewInvite({ permissions: 'viewer', expiryDays: 30, customCode: '' })
      loadInvites()
    } catch (error) {
      console.error('Create invite error:', error)
      toast.error(error.message)
    }
  }

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code)
    toast.success('Invite code copied to clipboard!')
  }

  const deleteInvite = async (inviteId, code) => {
    if (!confirm(`Delete invitation "${code}"? This cannot be undone.`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/admin-delete-invite', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ inviteId })
      })

      if (!response.ok) {
        throw new Error('Failed to delete invite')
      }

      toast.success('Invitation deleted successfully!')
      loadInvites()
    } catch (error) {
      console.error('Delete invite error:', error)
      toast.error('Failed to delete invitation')
    }
  }

  const getStatusIcon = (invite) => {
    if (invite.used_by) {
      return <CheckCircle className="w-4 h-4 text-green-400" />
    }
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
      return <XCircle className="w-4 h-4 text-red-400" />
    }
    return <Clock className="w-4 h-4 text-yellow-400" />
  }

  const getStatusText = (invite) => {
    if (invite.used_by) {
      return 'Used'
    }
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
      return 'Expired'
    }
    return 'Active'
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never expires'
    return new Date(dateString).toLocaleDateString()
  }

  const getPermissionIcon = (permission) => {
    const option = permissionOptions.find(opt => opt.value === permission)
    return option ? option.icon : Eye
  }

  if (loading) {
    return (
      <div className="card p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-netflix-red mx-auto mb-4"></div>
          <p className="text-gray-400">Loading invitations...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-white">Invitation Codes</h3>
            <p className="text-gray-400 text-sm">Manage invitation codes for new users</p>
          </div>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate Code
          </button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs uppercase">Total</p>
                <p className="text-white text-lg font-bold">{invites.length}</p>
              </div>
              <Key className="w-5 h-5 text-gray-500" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs uppercase">Active</p>
                <p className="text-green-400 text-lg font-bold">
                  {invites.filter(inv => !inv.used_by && (!inv.expires_at || new Date(inv.expires_at) > new Date())).length}
                </p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs uppercase">Used</p>
                <p className="text-blue-400 text-lg font-bold">
                  {invites.filter(inv => inv.used_by).length}
                </p>
              </div>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs uppercase">Expired</p>
                <p className="text-red-400 text-lg font-bold">
                  {invites.filter(inv => !inv.used_by && inv.expires_at && new Date(inv.expires_at) < new Date()).length}
                </p>
              </div>
              <XCircle className="w-5 h-5 text-red-500" />
            </div>
          </div>
        </div>

        {/* Invites List */}
        {invites.length === 0 ? (
          <div className="text-center py-12">
            <Key className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">No invitation codes created yet</p>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Your First Invitation
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {invites.map((invite) => {
              const PermissionIcon = getPermissionIcon(invite.permissions)
              return (
                <div key={invite.id} className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(invite)}
                      <span className="text-sm text-gray-400">{getStatusText(invite)}</span>
                    </div>
                    
                    <div className="font-mono text-lg text-white font-bold bg-gray-700 px-3 py-1 rounded">
                      {invite.code}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <PermissionIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-300 capitalize">{invite.permissions}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-400">
                      <div>Created: {formatDate(invite.created_at)}</div>
                      <div>Expires: {formatDate(invite.expires_at)}</div>
                      {invite.used_by && <div className="text-green-400">Used by user</div>}
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyInviteCode(invite.code)}
                        className="p-2 text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                        title="Copy invite code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      
                      {!invite.used_by && (
                        <button
                          onClick={() => deleteInvite(invite.id, invite.code)}
                          className="p-2 text-gray-400 hover:text-red-400 bg-gray-700 hover:bg-red-900 rounded transition-colors"
                          title="Delete invitation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create Invite Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Create Invitation</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Invitation Code (optional)
                </label>
                <input
                  type="text"
                  value={newInvite.customCode}
                  onChange={(e) => setNewInvite({...newInvite, customCode: e.target.value.toUpperCase()})}
                  className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-netflix-red focus:ring-1 focus:ring-netflix-red"
                  placeholder="Leave empty to auto-generate"
                />
                <p className="text-xs text-gray-500 mt-1">Example: FAMILY2024, SUMMER2025</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Permissions
                </label>
                <div className="space-y-2">
                  {permissionOptions.map((option) => {
                    const Icon = option.icon
                    return (
                      <label key={option.value} className="flex items-center p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700">
                        <input
                          type="radio"
                          name="permissions"
                          value={option.value}
                          checked={newInvite.permissions === option.value}
                          onChange={(e) => setNewInvite({...newInvite, permissions: e.target.value})}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                          newInvite.permissions === option.value 
                            ? 'border-netflix-red bg-netflix-red' 
                            : 'border-gray-600'
                        }`}>
                          {newInvite.permissions === option.value && (
                            <div className="w-full h-full rounded-full bg-white opacity-20"></div>
                          )}
                        </div>
                        <Icon className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-white font-medium">{option.label}</div>
                          <div className="text-xs text-gray-400">{option.description}</div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Expires In
                </label>
                <select
                  value={newInvite.expiryDays}
                  onChange={(e) => setNewInvite({...newInvite, expiryDays: parseInt(e.target.value)})}
                  className="w-full p-3 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-netflix-red focus:ring-1 focus:ring-netflix-red"
                >
                  {expiryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createInvite}
                className="bg-netflix-red hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Create Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default InvitesTab